import React from 'react';
import { connect } from 'react-redux';

// containers
import AuthCheckWrapper from 'app/shared/containers/AuthCheckWrapper';

// components
import HeadWrapper from 'app/shared/components/HeadWrapper';
import CompareGrid from 'app/shared/components/CompareGrid';
import CompetitorTable from 'app/shared/components/CompetitorTable';
import TrafficGraph from 'app/shared/components/TrafficGraph';
import BasicPieChart from 'app/shared/components/Graphs/BasicPieChart';
import SpinnerContainer from 'app/shared/components/SpinnerContainer';
import Notification from '@zg-rentals/multibrand-ui/lib/Notification';
import { withErrorBoundary } from 'app/shared/components/ErrorBoundary';

// Multibrand-UI Components
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Select from '@zg-rentals/multibrand-ui/lib/Select';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import StaticMap from '@zg-rentals/multibrand-ui/lib/StaticMap';

// Actions
import AnalyticsActions from 'app/shared/actions/AnalyticsActions';
import StaticMapActions from 'app/shared/actions/StaticMapActions';

// Selectors
import { AnalyticsSelectors, AppSelectors } from 'app/shared/selectors';

import LoadingConstants from 'app/shared/constants/LoadingConstants';

// Stylesheets
import './style.scss';

const DEFAULT_PAGE_TITLE = 'Competition';
const logger = log.child({ name: 'react' });

const CompetitionPage = ({
    isDev,
    dispatch,
    multifamilyListings,
    competitionData,
}) => {
    const { competitionDataLoadingState } = competitionData;
    const [isLoadingListings, setIsLoadingListings] = React.useState(true);

    const allListings = [
        {
            name: 'Select a Property',
            listingId: '',
        },
        ...multifamilyListings.listings,
    ];

    const [selectedListing, updateSelectedListing] = React.useState('');

    /* eslint-disable react-hooks/exhaustive-deps */
    React.useEffect(() => {
        dispatch(AnalyticsActions.getMultifamilyListings())
            .then(() => {
                setIsLoadingListings(false);
            });
        return () => dispatch(AnalyticsActions.clearPropertyCompetitionData());
    }, []);

    const updateListingId = (e) => {
        const selectedListingId = e.target.value;
        updateSelectedListing(selectedListingId);
        if (selectedListingId) {
            dispatch(AnalyticsActions.getPropertyCompetitionData({ listingId: selectedListingId }));
        } else {
            dispatch(AnalyticsActions.clearPropertyCompetitionData());
        }
        return null;
    };

    const signUrl = (url, brand) => {
        const params = { brandName: brand };

        if (isDev) {
            return Promise.resolve(url, brand);
        }
        return dispatch(StaticMapActions.signUrl(url, params))
            .catch(err => logger.error(err));
    };

    let userPropertyLat = null;
    let userPropertyLon = null;
    const competitorsMarkers = [];

    if (competitionData && competitionData.properties && competitionData.properties.length > 0) {
        userPropertyLat = competitionData.properties[0].lat;
        userPropertyLon = competitionData.properties[0].lon;

        competitionData.properties.forEach((property, id) => {
            if (id !== 0) {
                competitorsMarkers.push({
                    icon: 'primary',
                    locations: [
                        {
                            lat: property.lat,
                            lng: property.lon,
                            icon: 'secondary',
                        },
                    ],
                });
            }
            return null;
        });
    }

    const mapCompetitorContactPieData = () => competitionData.properties
        .map((property, i) => ({
            name: property.name,
            value: property.totalContacts,
            fill: i === 0 && '#3dca5a',
        }))
        .filter(dataPoint => dataPoint.value > 0);

    const renderContent = () => (
        <div>
            <div className="CompetitionPage-topCompetitors-widget">
                <Row>
                    <Col>
                        <Text type="h4" emphasize={true}>
                            Top Competitors
                        </Text>
                    </Col>
                </Row>
                <Row>
                    <Col className="TopCompetitors-map" sm={12} md={6} lg={6}>
                        <StaticMap
                            lat={userPropertyLat}
                            lng={userPropertyLon}
                            markers={competitorsMarkers}
                            scale={2}
                            zoom={13}
                            signUrl={signUrl}
                        />
                    </Col>
                    <Col className="TopCompetitors-contacts" sm={12} md={6} lg={6}>
                        <BasicPieChart
                            layout="vertical"
                            dataValueKey="totalContacts"
                            dataLabelKey="name"
                            data={mapCompetitorContactPieData()}
                        />
                    </Col>
                </Row>
            </div>
            <Row>
                <Col>
                    <Text type="h4" emphasize={true}>
                        Traffic Comparison
                    </Text>
                    <TrafficGraph properties={competitionData.properties} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <Text type="h4" emphasize={true}>
                        Rent Comparison
                    </Text>
                </Col>
            </Row>
            <Row>
                <Col>
                    {competitionData.properties.length > 0 &&
                        <CompareGrid properties={competitionData.properties.slice(0, 4)} />
                    }
                </Col>
            </Row>
            <Row>
                <Col>
                    <Text type="h4" emphasize={true}>
                        Special Offers &amp; Amenities Comparison
                    </Text>
                    <CompetitorTable properties={competitionData.properties} />
                </Col>
            </Row>
        </div>
    );

    const renderPageState = () => {
        switch (competitionDataLoadingState) {
            case LoadingConstants.LOADING:
                return <SpinnerContainer />;
            case LoadingConstants.SUCCESS:
                return renderContent();
            case LoadingConstants.ERROR:
                return (
                    <Notification type="error">
                        Error Loading data
                    </Notification>
                );
            default:
                return null;
        }
    };

    return (
        <AuthCheckWrapper requireStrongAuthentication={false}>
            <HeadWrapper title={DEFAULT_PAGE_TITLE} />
            <div className="CompetitionPage">
                <Row className="CompetitionPage-top-row">
                    <Col md={12} lg={5} xl={5}>
                        <Select
                            name="sort"
                            value={selectedListing || ''}
                            disabled={isLoadingListings}
                            onChange={updateListingId}
                        >
                            {allListings.map(listing => (
                                <option
                                    key={listing.listingId}
                                    value={listing.listingId}
                                >
                                    {listing.name}
                                </option>
                            ))}
                        </Select>
                    </Col>
                </Row>
                {renderPageState()}
            </div>
        </AuthCheckWrapper>
    );
};

const mapStateToProps = state => ({
    multifamilyListings: AnalyticsSelectors.makeMultifamilyListings(state),
    competitionData: AnalyticsSelectors.makeCompetitionData(state),
    isDev: AppSelectors.makeIsDev(state),
});

export default withErrorBoundary(connect(mapStateToProps)(CompetitionPage));
